import { MOCK_ACADEMIC_COURSES } from 'core/mocks/academic-courses.mock';
import { MOCK_CLASSES } from 'core/mocks/classes.mock';
import { MOCK_ENROLLMENTS } from 'core/mocks/enrollments.mock';
import { MOCK_INSTITUTIONS } from 'core/mocks/institutions.mock';
import { MOCK_SUBJECTS } from 'core/mocks/subjects.mock';
import { MOCK_USERS } from 'core/mocks/users.mock';
import {
  PlatformSearchGroup,
  PlatformSearchItem,
  PlatformSearchResult,
} from 'core/models/platform-search-result.model';
import { ROLE_LABELS, Role } from 'core/models/role.model';
import {
  defaultIconForSearchType,
  labelForSearchType,
} from 'core/utils/platform-search-route.util';
import { includesPtBrSearch } from 'shared/utils/normalize-pt-br-text.util';

const MAX_PER_GROUP = 10;

function pushGroup(
  groups: PlatformSearchGroup[],
  type: PlatformSearchGroup['type'],
  items: readonly PlatformSearchItem[],
): void {
  if (items.length === 0) {
    return;
  }
  groups.push({
    type,
    label: labelForSearchType(type),
    items: items.slice(0, MAX_PER_GROUP),
  });
}

function institutionName(id: string | undefined): string {
  if (!id) {
    return '-';
  }
  return MOCK_INSTITUTIONS.find((item) => item.id === id)?.name ?? '-';
}

function userName(id: string): string {
  return MOCK_USERS.find((item) => item.id === id)?.name ?? id;
}

function className(id: string): string {
  return MOCK_CLASSES.find((item) => item.id === id)?.name ?? id;
}

function routeHintForRole(role: Role): string {
  switch (role) {
    case 'STUDENT':
      return 'students';
    case 'TEACHER':
      return 'teachers';
    case 'GUARDIAN':
      return 'guardians';
    default:
      return 'users';
  }
}

/** Busca local sobre mocks - fallback enquanto `GET /search` nao existe. */
export function buildMockPlatformSearchResult(
  query: string,
): PlatformSearchResult {
  const q = query.trim();
  const groups: PlatformSearchGroup[] = [];

  if (!q) {
    return { query: q, total: 0, groups: [] };
  }

  const users: PlatformSearchItem[] = MOCK_USERS.filter(
    (item) =>
      includesPtBrSearch(item.name, q) ||
      includesPtBrSearch(item.email ?? '', q) ||
      includesPtBrSearch(item.person?.cpf, q) ||
      includesPtBrSearch(ROLE_LABELS[item.role], q),
  ).map((item) => ({
    id: item.id,
    type: 'user',
    title: item.name,
    subtitle: `${ROLE_LABELS[item.role]} - ${institutionName(item.institutionId)}`,
    iconClass: defaultIconForSearchType('user'),
    routeHint: routeHintForRole(item.role),
    meta: {
      role: item.role,
      institutionId: item.institutionId,
    },
  }));

  const classes: PlatformSearchItem[] = MOCK_CLASSES.filter(
    (item) =>
      includesPtBrSearch(item.name, q) ||
      includesPtBrSearch(item.grade, q) ||
      includesPtBrSearch(item.academicCourseName, q) ||
      includesPtBrSearch(String(item.schoolYear), q),
  ).map((item) => ({
    id: item.id,
    type: 'class',
    title: item.name,
    subtitle: `${item.schoolYear} - ${institutionName(item.institutionId)}`,
    iconClass: defaultIconForSearchType('class'),
    routeHint: 'classes',
    meta: { institutionId: item.institutionId },
  }));

  const enrollments: PlatformSearchItem[] = MOCK_ENROLLMENTS.filter((item) => {
    const student = userName(item.studentId);
    const klass = className(item.classId);
    return (
      includesPtBrSearch(student, q) ||
      includesPtBrSearch(klass, q) ||
      includesPtBrSearch(item.studentName, q) ||
      includesPtBrSearch(item.className, q) ||
      includesPtBrSearch(String(item.schoolYear), q)
    );
  }).map((item) => {
    const student = item.studentName ?? userName(item.studentId);
    const klass = item.className ?? className(item.classId);
    return {
      id: item.id,
      type: 'enrollment' as const,
      title: `${student} - ${klass}`,
      subtitle: `Matrícula ${item.schoolYear} - ${institutionName(item.institutionId)}`,
      iconClass: defaultIconForSearchType('enrollment'),
      routeHint: 'enrollments',
      meta: { institutionId: item.institutionId },
    };
  });

  const institutions: PlatformSearchItem[] = MOCK_INSTITUTIONS.filter(
    (item) =>
      includesPtBrSearch(item.name, q) ||
      includesPtBrSearch(item.tradeName, q) ||
      includesPtBrSearch(item.inepCode, q) ||
      includesPtBrSearch(item.city, q),
  ).map((item) => ({
    id: item.id,
    type: 'institution',
    title: item.name,
    subtitle: [item.city, item.state].filter(Boolean).join(' - ') || '-',
    iconClass: defaultIconForSearchType('institution'),
    routeHint: 'institutions',
  }));

  const academicCourses: PlatformSearchItem[] = MOCK_ACADEMIC_COURSES.filter(
    (item) =>
      includesPtBrSearch(item.name, q) ||
      includesPtBrSearch(item.description, q) ||
      includesPtBrSearch(item.level, q),
  ).map((item) => ({
    id: item.id,
    type: 'academic_course',
    title: item.name,
    subtitle: institutionName(item.institutionId),
    iconClass: defaultIconForSearchType('academic_course'),
    routeHint: 'academic-courses',
    meta: { institutionId: item.institutionId },
  }));

  const subjects: PlatformSearchItem[] = MOCK_SUBJECTS.filter(
    (item) =>
      includesPtBrSearch(item.name, q) ||
      includesPtBrSearch(item.knowledgeArea, q),
  ).map((item) => ({
    id: item.id,
    type: 'subject',
    title: item.name,
    subtitle: institutionName(item.institutionId),
    iconClass: defaultIconForSearchType('subject'),
    routeHint: 'subjects',
    meta: { institutionId: item.institutionId },
  }));

  pushGroup(groups, 'user', users);
  pushGroup(groups, 'class', classes);
  pushGroup(groups, 'enrollment', enrollments);
  pushGroup(groups, 'institution', institutions);
  pushGroup(groups, 'academic_course', academicCourses);
  pushGroup(groups, 'subject', subjects);

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  return { query: q, total, groups };
}
